/**
 * Appointment domain types. Mirrors what the (future) `/api/v1/appointments`
 * surface will return. Until that endpoint lands the mock adapter at
 * `shared/api/mock/appointments.ts` serves these objects.
 */

export type AppointmentStatus =
  | "REQUESTED"
  | "CONFIRMED"
  | "CHECKED_IN"
  | "COMPLETED"
  | "NO_SHOW"
  | "CANCELLED";

/**
 * Trạng thái phân công của lịch hẹn (Booking Policy). `null` khi không cần
 * lễ tân phân công (đã đủ hoặc chính sách không yêu cầu).
 */
export type AppointmentAssignState =
  | "PENDING_DOCTOR"
  | "PENDING_ROOM"
  | "ASSIGNED"
  | null;

export interface AppointmentSummary {
  id: string;
  patientId: string;
  patientName: string;
  patientPhone: string | null;
  doctorId: string;
  doctorName: string;
  serviceId: string | null;
  /** Dịch vụ CHÍNH (phần tử đầu của `serviceNames`). */
  serviceName: string | null;
  /**
   * Tên MỌI dịch vụ đã chọn, theo thứ tự đã chọn. Danh sách và lịch tuần trả nó; các phản hồi
   * tạo/sửa (và máy chủ cũ) không có → `undefined`/`null` = chưa nạp, màn hình lùi về `serviceName`.
   * Rỗng `[]` là một trạng thái thật: lịch không chọn dịch vụ nào. Xem `serviceNamesOf()`.
   */
  serviceNames?: string[] | null;
  /** ISO datetime, Asia/Ho_Chi_Minh */
  startAt: string;
  /** Minutes */
  durationMinutes: number;
  status: AppointmentStatus;
  notes: string | null;
  /**
   * True while the patient is being examined (an OPEN/IN_PROGRESS encounter exists). The
   * appointment status stays CHECKED_IN — this flag lets the Lịch hẹn show "Đang khám".
   */
  inExam: boolean;
  /** Trạng thái phân công (Booking Policy) — điều khiển nút "Phân công". */
  assignState?: AppointmentAssignState;
  /** Khoa của lịch hẹn (Booking Policy). */
  departmentId?: string | null;
  /** Tên khoa (đọc từ GET /v1/appointments/{id}). */
  departmentName?: string | null;
  /** Phòng khám đã cấp cho lịch hẹn (nếu có). */
  roomId?: string | null;
  roomName?: string | null;
  /**
   * #19934 — STT VÀO PHÒNG: lịch đã có phòng; dãy theo Phạm vi STT (Chung / Theo từng phòng), đếm
   * lại theo Ngày/Buổi. null = lịch chưa có phòng (dùng `registrationNo`).
   */
  roomNo?: number | null;
  /**
   * #19934 — STT QUẦY TIẾP ĐÓN. Lịch đặt không phòng nhận số này ngay khi đặt (khi phòng khám bật
   * cách đánh số mới); người tới thẳng quầy nhận lúc tiếp đón. Xem `pages/appointments/stt.ts`.
   */
  registrationNo?: number | null;
  /**
   * Mã đặt lịch BỆNH NHÂN đọc cho lễ tân (#19396) — 8 ký tự hex đầu của `id`, viết hoa. Đây là mã
   * Zalo Mini App hiện ở "Lịch sử khám", và ô tìm kiếm chung của HIS tra được theo nó.
   *
   * Máy chủ tính từ `id` và trả kèm (`BookingCode`), FE KHÔNG tự suy: hai công thức là hai công
   * thức sẽ lệch. Khác hẳn `roomNo` — đó là STT hàng chờ, lặp lại mỗi ngày.
   */
  bookingCode?: string | null;
  /** Trạng thái gọi trong hàng chờ phòng: CALLED / HELD / SKIPPED, null = đang chờ (#18602). */
  queueState?: "CALLED" | "HELD" | "SKIPPED" | null;
  /** Lúc được gọi gần nhất. */
  calledAt?: string | null;
}

/**
 * Một dịch vụ bệnh nhân yêu cầu trong lịch hẹn (#19237). `priceVnd` null = chưa niêm yết
 * ("Liên hệ"), khác hẳn 0đ (miễn phí) — gộp hai thứ đó lại là nói sai với bệnh nhân.
 */
export interface RequestedService {
  serviceId: string;
  serviceName: string;
  priceVnd: number | null;
  /**
   * Đơn giá ĐÃ THU của dịch vụ này trên `linkedInvoice` — null khi chưa có hoá đơn hoặc hoá đơn
   * không có dòng cho nó. Màn hình ưu tiên nó hơn `priceVnd`: dữ liệu nạp từ SalePro mang giá trên
   * từng phiếu (một dịch vụ bán ở 75.000 / 70.000 / 0đ) còn danh mục ghi 0đ, nên giá niêm yết nói
   * sai về một lượt đã thu tiền. 0đ trên hoá đơn vẫn là 0đ, không phải "chưa niêm yết". Tuỳ chọn
   * vì máy chủ cũ không trả.
   */
  invoicedPriceVnd?: number | null;
}

/** Hoá đơn còn hiệu lực đã lập cho lượt khám của lịch hẹn — đủ để ghi "theo hoá đơn …" và dẫn tới nó. */
export interface LinkedInvoice {
  invoiceId: string;
  invoiceNumber: string;
  status: string;
}

export interface AppointmentDetail extends AppointmentSummary {
  patientCode: string | null;
  patientDateOfBirth: string | null;
  createdAt: string;
  updatedAt: string;
  /** History trail. */
  events: AppointmentEvent[];
  /**
   * Toàn bộ dịch vụ bệnh nhân yêu cầu, theo đúng thứ tự đã chọn (#19237); phần tử đầu là dịch vụ
   * chính và trùng `serviceId`/`serviceName`. Rỗng = lịch chưa chọn dịch vụ nào, một trạng thái
   * THẬT (lượt chuyển khoa, #18930).
   *
   * Tuỳ chọn vì `GET /v1/appointments/{id}` là nguồn duy nhất trả nó: các màn dựng
   * `AppointmentDetail` từ chỗ khác (assign, create) không có mảng này.
   */
  services?: RequestedService[];
  /** Hoá đơn của lượt khám (qua lần khám); null/vắng = chưa có. Chỉ `GET /v1/appointments/{id}` trả. */
  linkedInvoice?: LinkedInvoice | null;
}

export interface AppointmentEvent {
  occurredAt: string;
  actor: string;
  action: string;
  fromStatus?: AppointmentStatus;
  toStatus?: AppointmentStatus;
  note?: string;
}

export interface AppointmentCreateRequest {
  patientId: string;
  /** Optional — null/empty means "bác sĩ bất kỳ" (assigned at check-in). */
  doctorId?: string | null;
  serviceId?: string | null;
  startAt: string;
  durationMinutes: number;
  notes?: string | null;
  /** Optional Khoa/Phòng captured at booking per the clinic's booking-policy dimensions
   *  (#18546/#18573). room_no (số phòng) is still allocated at check-in, not here. */
  departmentId?: string | null;
  roomId?: string | null;
  /** Hình thức khám (#18673) — "BHYT" | "SERVICE" | null (dimension HIDDEN or left blank). */
  paymentType?: "BHYT" | "SERVICE" | null;
  /** Only meaningful when paymentType is "BHYT". */
  bhytCardNo?: string | null;
  /** Hình thức đăng ký cho tenant MQ (#18674) — "COUNTER" | "ROOM" | null. Chỉ có ý nghĩa cho
   *  tenant kết nối MQ; null/tenant khác thì bị bỏ qua. */
  registrationType?: "COUNTER" | "ROOM" | null;
}

export interface AppointmentRescheduleRequest {
  startAt: string;
  durationMinutes: number;
  reason?: string | null;
}

export interface AppointmentListFilter {
  q?: string | null;
  status?: AppointmentStatus | null;
  patientId?: string | null;
  doctorId?: string | null;
  /** ISO date — yyyy-MM-dd */
  fromDate?: string | null;
  toDate?: string | null;
}

export interface DoctorOption {
  id: string;
  name: string;
  specialty: string | null;
}

export interface ServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  priceVnd: number;
}

/**
 * Tên các dịch vụ để in trên một dòng danh sách: đủ danh sách khi máy chủ đã nạp, còn không thì
 * dịch vụ chính (một phần tử) — không bao giờ ẩn dịch vụ chính chỉ vì mảng chưa về.
 */
export function serviceNamesOf(a: Pick<AppointmentSummary, "serviceName" | "serviceNames">): string[] {
  if (a.serviceNames && a.serviceNames.length > 0) return a.serviceNames;
  return a.serviceName ? [a.serviceName] : [];
}
